import { useEffect, useState } from 'react';

export interface TelegramThemeParams {
  bg_color?: string;
  text_color?: string;
  hint_color?: string;
  link_color?: string;
  button_color?: string;
  button_text_color?: string;
  secondary_bg_color?: string;
  header_bg_color?: string;
  accent_text_color?: string;
  section_bg_color?: string;
  section_header_text_color?: string;
  subtitle_text_color?: string;
  destructive_text_color?: string;
}

export interface TelegramThemeState {
  available: boolean;
  colorScheme: 'light' | 'dark' | null;
  params: TelegramThemeParams;
}

const EMPTY: TelegramThemeState = {
  available: false,
  colorScheme: null,
  params: {}
};

function readState(): TelegramThemeState {
  const tg = (window as any)?.Telegram?.WebApp;
  if (!tg) return EMPTY;
  return {
    available: true,
    colorScheme: (tg.colorScheme as 'light' | 'dark') ?? null,
    params: (tg.themeParams as TelegramThemeParams) ?? {}
  };
}

function applyCssVars(params: TelegramThemeParams): void {
  const root = document.documentElement;
  const map: Record<string, string | undefined> = {
    '--tg-bg-color': params.bg_color,
    '--tg-text-color': params.text_color,
    '--tg-hint-color': params.hint_color,
    '--tg-link-color': params.link_color,
    '--tg-button-color': params.button_color,
    '--tg-button-text-color': params.button_text_color,
    '--tg-secondary-bg-color': params.secondary_bg_color,
    '--tg-header-bg-color': params.header_bg_color,
    '--tg-accent-text-color': params.accent_text_color,
    '--tg-section-bg-color': params.section_bg_color,
    '--tg-subtitle-text-color': params.subtitle_text_color,
    '--tg-destructive-text-color': params.destructive_text_color
  };
  Object.entries(map).forEach(([k, v]) => {
    if (v) root.style.setProperty(k, v);
    else root.style.removeProperty(k);
  });
}

/**
 * Reads Telegram.WebApp.colorScheme + themeParams, exposes them, and keeps
 * them in sync via the `themeChanged` event. Also writes the params to CSS
 * variables on :root so Tailwind / inline styles can pick them up via
 * `var(--tg-bg-color)` etc.
 */
export default function useTelegramTheme(): TelegramThemeState {
  const [state, setState] = useState<TelegramThemeState>(() => {
    const s = readState();
    if (s.available) applyCssVars(s.params);
    return s;
  });

  useEffect(() => {
    const tg = (window as any)?.Telegram?.WebApp;
    if (!tg?.onEvent) return;

    const update = (): void => {
      const next = readState();
      applyCssVars(next.params);
      setState(next);
    };

    tg.onEvent('themeChanged', update);
    update();
    return () => {
      tg.offEvent?.('themeChanged', update);
    };
  }, []);

  return state;
}
